import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as EmptyIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
  PlayArrow as GenerateIcon,
  CompareArrows as CompareIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import * as Diff from 'diff';
import { useApiClient } from '../../config/axios';
import axios from 'axios';

// ===============================================
// TypeScript Interfaces
// ===============================================

interface PatternSummary {
  pattern_id: number;
  pattern_key: string | null;
  source_file: string | null;
}

interface GeneratedPivotSummary {
  generated_pattern_id: number;
  template_pattern_id: number | null;
  pivot_name: string;
  template_name: string | null;
  unique_id: string;
  source: string;
  created_at: string;
  part_count: number;
}

interface HeaderModel {
  id?: string | null;
  bottomLevels: string[];
  computed: Record<string, any>;
  suppressMemoryManagement?: boolean | null;
  runSerially?: boolean | null;
}

interface SectionItem {
  name: string;
  content: string;
  part_kind: string;
  part_ordinal: number;
}

interface OrderedSection {
  section: string;
  items: SectionItem[];
}

interface AddPartRequest {
  section_id: string;
  part_name?: string;
  part_kind?: string;
  text: string;
}

interface AddPartResponse {
  pattern_id: number;
  part_ordinal: number;
}

// ===============================================
// API Helper Functions
// ===============================================

async function fetchPatterns(): Promise<PatternSummary[]> {
  const response = await axios.get('/patterns');
  return response.data;
}

async function fetchGeneratedPivots(topNav: string, leftNav: string, view: string): Promise<GeneratedPivotSummary[]> {
  const response = await axios.get(`/pivot-navigation/${encodeURIComponent(topNav)}/${encodeURIComponent(leftNav)}/${encodeURIComponent(view)}/pivots`);
  return response.data.pivots || [];
}

async function fetchHeader(patternId: number): Promise<HeaderModel> {
  const response = await axios.get(`/patterns/${patternId}/header`);
  return response.data;
}

async function fetchGeneratedHeader(generatedPatternId: number): Promise<HeaderModel> {
  const response = await axios.get(`/generated-pivots/${generatedPatternId}/header`);
  return response.data;
}

async function fetchSections(patternId: number): Promise<OrderedSection[]> {
  const response = await axios.get(`/patterns/${patternId}/sections`);
  return response.data;
}

async function fetchGeneratedSections(generatedPatternId: number): Promise<{ sections: OrderedSection[]; header: HeaderModel | null }> {
  const response = await axios.get(`/generated-pivots/${generatedPatternId}`);
  const data = response.data;
  return {
    sections: data.sections || [],
    header: data.header || null
  };
}

async function addPart(patternId: number, request: AddPartRequest): Promise<AddPartResponse> {
  const response = await axios.post(`/patterns/${patternId}/sections`, request);
  return response.data;
}

async function generatePivots(): Promise<{ success: boolean; job_id?: string; status?: string; message?: string; pivots_generated?: number; parts_generated?: number; error?: string }> {
  const response = await axios.post('/generate-pivots', { created_by: 'user' });
  return response.data;
}

async function fetchNavigationOptions(): Promise<{ topNavs: string[]; leftNavs: Record<string, string[]>; views: Record<string, string[]> }> {
  const response = await axios.get('/pivot-navigation-tree');
  const tree = response.data.tree || [];

  const topNavs: string[] = [];
  const leftNavs: Record<string, string[]> = {};
  const views: Record<string, string[]> = {};

  tree.forEach((topNode: any) => {
    topNavs.push(topNode.top_nav);
    leftNavs[topNode.top_nav] = topNode.left_navs.map((ln: any) => ln.left_nav);

    topNode.left_navs.forEach((leftNavNode: any) => {
      const key = `${topNode.top_nav}|${leftNavNode.left_nav}`;
      views[key] = leftNavNode.views.map((v: any) => v.view);
    });
  });

  return { topNavs, leftNavs, views };
}

async function getUniqueIdForNavigation(topNav: string, leftNav: string, view: string): Promise<string | null> {
  const response = await axios.get(`/pivot-navigation/${encodeURIComponent(topNav)}/${encodeURIComponent(leftNav)}/${encodeURIComponent(view)}/pivots`);
  if (response.data.pivots && response.data.pivots.length > 0) {
    return response.data.pivots[0].unique_id;
  }
  return null;
}

// ===============================================
// Sub-Components (from PivotManager)
// ===============================================

interface SectionOverviewCardProps {
  sections: OrderedSection[];
  onSectionSelect: (sectionName: string) => void;
}

interface SectionFocusHeaderProps {
  sectionName: string;
  itemCount: number;
  totalSections: number;
  onBackToAll: () => void;
}

interface SubsectionOverviewProps {
  section: OrderedSection;
  onItemSelect: (item: SectionItem) => void;
  onBackToSections: () => void;
}

interface ItemDetailViewProps {
  item: SectionItem;
  sectionName: string;
  onBackToItems: () => void;
  onRefresh: () => void;
  mode: 'templates' | 'generated';
  editingItem: string | null;
  editContent: string;
  onEdit: (itemName: string, content: string) => void;
  onSave: (itemName: string) => void;
  onCancel: () => void;
  onEditContentChange: (content: string) => void;
  saving: boolean;
}

function SectionFocusHeader({ sectionName, itemCount, totalSections, onBackToAll }: SectionFocusHeaderProps) {
  return (
    <Paper sx={{ p: 3, mb: 3, backgroundColor: 'primary.50', border: '1px solid', borderColor: 'primary.main' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={onBackToAll}
            sx={{ borderColor: 'primary.main', color: 'primary.main' }}
          >
            Back to All Sections
          </Button>
          <Typography variant="h5" fontWeight="bold" color="primary.main">
            Focusing on: {sectionName}
          </Typography>
        </Box>
        <Button
          variant="text"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={onBackToAll}
          sx={{ color: 'primary.main' }}
        >
          View Other Sections
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Items in this section:
          </Typography>
          <Typography variant="h6" color="primary.main" fontWeight="medium">
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Total sections available:
          </Typography>
          <Typography variant="h6" color="text.primary" fontWeight="medium">
            {totalSections} sections
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2, p: 2, backgroundColor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">
          💡 <strong>Focus Mode:</strong> You're now viewing only the "{sectionName}" section. Use the controls above to navigate back or the dropdown to switch to another section.
        </Typography>
      </Box>
    </Paper>
  );
}

function SectionOverviewCard({ sections, onSectionSelect }: SectionOverviewCardProps) {
  return (
    <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.paper' }}>
      <Typography variant="h6" gutterBottom>
        Section Overview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Click on any section below to view its items, or use the dropdown above to switch between sections.
      </Typography>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
        {sections.map((section) => {
          const hasContent = section.items.length > 0;
          return (
            <Paper
              key={section.section}
              variant="outlined"
              sx={{
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  elevation: 2,
                  backgroundColor: 'action.hover',
                  transform: 'translateY(-1px)',
                },
                border: hasContent ? '1px solid' : '1px dashed',
                borderColor: hasContent ? 'primary.main' : 'divider',
              }}
              onClick={() => onSectionSelect(section.section)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {hasContent ? (
                  <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                ) : (
                  <EmptyIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                )}
                <Typography variant="body1" fontWeight="medium" sx={{ flexGrow: 1 }}>
                  {section.section}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {section.items.length} {section.items.length === 1 ? 'item' : 'items'}
                </Typography>
                {hasContent && (
                  <Typography variant="caption" color="primary.main" fontWeight="medium">
                    View items →
                  </Typography>
                )}
                {!hasContent && (
                  <Typography variant="caption" color="text.disabled">
                    Empty section
                  </Typography>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>

      {sections.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No sections available for this pattern.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function SubsectionOverview({ section, onItemSelect, onBackToSections }: SubsectionOverviewProps) {
  const getContentPreview = (content: string) => {
    const lines = content.split('\n');
    return lines.slice(0, 3).join('\n');
  };

  return (
    <>
      <Paper sx={{ p: 3, mb: 3, backgroundColor: 'primary.50', border: '1px solid', borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={onBackToSections}
              sx={{ borderColor: 'primary.main', color: 'primary.main' }}
            >
              Back to Sections
            </Button>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              Items in: {section.section}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total items:
            </Typography>
            <Typography variant="h6" color="primary.main" fontWeight="medium">
              {section.items.length} {section.items.length === 1 ? 'item' : 'items'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2, p: 2, backgroundColor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            💡 <strong>Item Overview:</strong> Click on any item below to select and edit it. Each tile shows a preview of the content.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.paper' }}>
        <Typography variant="h6" gutterBottom>
          Items in {section.section}
        </Typography>

        {section.items.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
            {section.items.map((item) => {
              const contentLines = item.content.split('\n').length;
              const contentChars = item.content.length;
              const preview = getContentPreview(item.content);
              const isLongContent = item.content.split('\n').length > 3;

              return (
                <Paper
                  key={`${section.section}:${item.part_ordinal}:${item.name}`}
                  variant="outlined"
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      elevation: 3,
                      backgroundColor: 'action.hover',
                      transform: 'translateY(-2px)',
                      borderColor: 'primary.main',
                    },
                    border: '1px solid',
                    borderColor: 'divider',
                    minHeight: 200,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onClick={() => onItemSelect(item)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {item.part_kind === 'sql' ? (
                      <CodeIcon sx={{ fontSize: 18, color: 'info.main' }} />
                    ) : (
                      <DescriptionIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                    )}
                    <Typography variant="body1" fontWeight="bold" sx={{ flexGrow: 1 }} noWrap>
                      {item.name}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={`#${item.part_ordinal}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                    <Chip
                      label={item.part_kind.toUpperCase()}
                      size="small"
                      color={item.part_kind === 'sql' ? 'info' : 'secondary'}
                      sx={{ fontSize: '0.75rem' }}
                    />
                    <Chip
                      label={`${contentLines}L • ${contentChars}C`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Content Preview:
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        margin: 0,
                        padding: 1.5,
                        backgroundColor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        maxHeight: 100,
                      }}
                    >
                      {preview}{isLongContent && '\n...'}
                    </Box>
                  </Box>

                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="primary.main" fontWeight="medium">
                      Click to select and view →
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No items found in this section.
            </Typography>
            <Typography variant="body2" color="text.disabled">
              This section is empty.
            </Typography>
          </Box>
        )}
      </Paper>
    </>
  );
}

function ItemDetailView({ item, sectionName, onBackToItems, onRefresh, mode, editingItem, editContent, onEdit, onSave, onCancel, onEditContentChange, saving }: ItemDetailViewProps) {
  const contentLines = item.content.split('\n').length;
  const contentChars = item.content.length;

  return (
    <>
      <Paper sx={{ p: 3, mb: 3, backgroundColor: 'primary.50', border: '1px solid', borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={onBackToItems}
              sx={{ borderColor: 'primary.main', color: 'primary.main' }}
            >
              Back to Items
            </Button>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              Viewing: {item.name}
            </Typography>
          </Box>
          {/* Edit/Save buttons - only show for generated pivots */}
          {mode === 'generated' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {editingItem === item.name ? (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={() => onSave(item.name)}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={onCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => onEdit(item.name, item.content)}
                >
                  Edit
                </Button>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Section:
            </Typography>
            <Typography variant="h6" color="primary.main" fontWeight="medium">
              {sectionName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Ordinal:
            </Typography>
            <Typography variant="h6" color="primary.main" fontWeight="medium">
              #{item.part_ordinal}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Type:
            </Typography>
            <Typography variant="h6" color="primary.main" fontWeight="medium">
              {item.part_kind.toUpperCase()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Size:
            </Typography>
            <Typography variant="h6" color="primary.main" fontWeight="medium">
              {contentLines} lines • {contentChars} chars
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            📝 <strong>Item Detail:</strong> Full content view for this item.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.paper' }}>
        <Typography variant="h6" gutterBottom>
          Content
        </Typography>

        {/* Show textarea when editing, otherwise show pre */}
        {editingItem === item.name ? (
          <TextField
            multiline
            fullWidth
            value={editContent}
            onChange={(e) => onEditContentChange(e.target.value)}
            minRows={20}
            maxRows={40}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }
            }}
          />
        ) : (
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              margin: 0,
              padding: 3,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              lineHeight: 1.5,
              maxHeight: '60vh',
              overflow: 'auto',
            }}
          >
            {item.content}
          </Box>
        )}
      </Paper>
    </>
  );
}

function HeaderCard({ header }: { header: HeaderModel | null }) {
  if (!header) return null;

  return (
    <Paper sx={{ p: 4, mb: 3, backgroundColor: 'background.paper' }}>
      <Typography variant="h5" gutterBottom>Header</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, fontSize: '0.875rem' }}>
        <Box>
          <Typography variant="body2" fontWeight="medium">id:</Typography>
          <Typography variant="body2" color={header.id ? 'text.primary' : 'text.secondary'}>
            {header.id || '—'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontWeight="medium">runSerially:</Typography>
          <Typography variant="body2">
            {String(header.runSerially)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontWeight="medium">suppressMemoryManagement:</Typography>
          <Typography variant="body2">
            {String(header.suppressMemoryManagement)}
          </Typography>
        </Box>
        <Box sx={{ gridColumn: { md: 'span 2' } }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>bottomLevels:</Typography>
          <Box
            component="pre"
            sx={{
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              backgroundColor: 'grey.50',
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              margin: 0,
            }}
          >
            {header.bottomLevels.join('\n') || '—'}
          </Box>
        </Box>
        <Box sx={{ gridColumn: { md: 'span 2' } }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>computed:</Typography>
          <Box
            component="pre"
            sx={{
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              backgroundColor: 'grey.50',
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              margin: 0,
            }}
          >
            {JSON.stringify(header.computed || {}, null, 2)}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

function SectionComponent({
  patternId,
  section,
  onRefresh,
  mode,
  editingItem,
  editContent,
  onEdit,
  onSave,
  onCancel,
  onEditContentChange,
  saving
}: {
  patternId: number;
  section: OrderedSection;
  onRefresh: () => void;
  mode: 'templates' | 'generated';
  editingItem: string | null;
  editContent: string;
  onEdit: (itemName: string, content: string) => void;
  onSave: (itemName: string) => void;
  onCancel: () => void;
  onEditContentChange: (content: string) => void;
  saving: boolean;
}) {
  return (
    <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.paper' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">{section.section}</Typography>
          <Chip
            label={`${section.items.length} ${section.items.length === 1 ? 'item' : 'items'}`}
            size="small"
            color={section.items.length > 0 ? 'primary' : 'default'}
            variant="outlined"
          />
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        {section.items.map((item, index) => {
          const itemId = `${section.section}:${item.part_ordinal}:${item.name}`;
          const contentLines = item.content.split('\n').length;
          const contentChars = item.content.length;

          return (
            <Paper
              key={itemId}
              variant="outlined"
              sx={{
                p: 3,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Typography variant="body1" fontWeight="medium" color="text.primary">
                    {item.name}
                  </Typography>
                  <Chip
                    label={`#${item.part_ordinal}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                  <Chip
                    label={item.part_kind.toUpperCase()}
                    size="small"
                    color={item.part_kind === 'sql' ? 'info' : 'secondary'}
                    sx={{ fontSize: '0.75rem' }}
                  />
                  <Chip
                    label={`${contentLines} lines • ${contentChars} chars`}
                    size="small"
                    variant="outlined"
                    color="default"
                    sx={{ fontSize: '0.75rem', ml: 1 }}
                  />
                </Box>
                {/* Edit/Save buttons - only show for generated pivots */}
                {mode === 'generated' && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {editingItem === item.name ? (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => onSave(item.name)}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={onCancel}
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => onEdit(item.name, item.content)}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {/* Show textarea when editing, otherwise show pre */}
              {editingItem === item.name ? (
                <TextField
                  multiline
                  fullWidth
                  value={editContent}
                  onChange={(e) => onEditContentChange(e.target.value)}
                  minRows={10}
                  maxRows={30}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    }
                  }}
                />
              ) : (
                <Box
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    margin: 0,
                    padding: 2,
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    color: 'text.primary',
                    lineHeight: 1.5,
                  }}
                >
                  {item.content}
                </Box>
              )}
            </Paper>
          );
        })}
        {!section.items.length && (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No parts found for this section.
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ===============================================
// Diff Viewer Component
// ===============================================

interface DiffViewerProps {
  templateContent: string;
  generatedContent: string;
  itemName: string;
}

function DiffViewer({ templateContent, generatedContent, itemName }: DiffViewerProps) {
  const diffResult = Diff.diffLines(templateContent, generatedContent);

  let addedCount = 0;
  let removedCount = 0;
  diffResult.forEach(part => {
    if (part.added) addedCount += part.count || 0;
    if (part.removed) removedCount += part.count || 0;
  });

  return (
    <Box>
      {/* Diff Summary */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {addedCount > 0 && (
          <Chip
            label={`+${addedCount} lines added`}
            size="small"
            sx={{ backgroundColor: '#d4edda', color: '#155724' }}
          />
        )}
        {removedCount > 0 && (
          <Chip
            label={`-${removedCount} lines removed`}
            size="small"
            sx={{ backgroundColor: '#f8d7da', color: '#721c24' }}
          />
        )}
        {addedCount === 0 && removedCount === 0 && (
          <Chip
            label="No differences"
            size="small"
            sx={{ backgroundColor: '#d4edda', color: '#155724' }}
          />
        )}
      </Box>

      {/* Unified Diff View */}
      <Box
        component="pre"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          whiteSpace: 'pre-wrap',
          backgroundColor: '#f5f5f5',
          p: 2,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          margin: 0,
          maxHeight: '500px',
          overflow: 'auto',
          lineHeight: 1.6
        }}
      >
        {diffResult.map((part, index) => (
          <Box
            key={index}
            component="span"
            sx={{
              display: 'block',
              backgroundColor: part.added
                ? '#d4edda'
                : part.removed
                  ? '#f8d7da'
                  : 'transparent',
              color: part.added
                ? '#155724'
                : part.removed
                  ? '#721c24'
                  : 'inherit',
              paddingLeft: 1,
              borderLeft: part.added || part.removed ? '3px solid' : 'none',
              borderLeftColor: part.added ? '#28a745' : part.removed ? '#dc3545' : 'transparent'
            }}
          >
            {part.added && '+ '}
            {part.removed && '- '}
            {part.value}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ===============================================
// Side-by-Side Diff Viewer Component
// ===============================================

interface SideBySideDiffViewerProps {
  templateContent: string;
  generatedContent: string;
  itemName: string;
  showHeaders?: boolean;
  showSummary?: boolean;
  inline?: boolean;
  side?: 'template' | 'generated';
}

function SideBySideDiffViewer({ templateContent, generatedContent, itemName, showHeaders = true, showSummary = true, inline = false, side = 'template' }: SideBySideDiffViewerProps) {
  const diffResult = Diff.diffLines(templateContent, generatedContent);

  let addedCount = 0;
  let removedCount = 0;
  diffResult.forEach(part => {
    if (part.added) addedCount += part.count || 0;
    if (part.removed) removedCount += part.count || 0;
  });

  // Build side-by-side arrays
  const templateLines: Array<{ content: string; type: 'normal' | 'removed' | 'blank' }> = [];
  const generatedLines: Array<{ content: string; type: 'normal' | 'added' | 'blank' }> = [];

  diffResult.forEach(part => {
    const lines = part.value.split('\n');
    // Remove last empty line if it exists (from split)
    if (lines[lines.length - 1] === '') {
      lines.pop();
    }

    if (part.added) {
      // Added lines: blank on template side, content on generated side
      lines.forEach(line => {
        templateLines.push({ content: '', type: 'blank' });
        generatedLines.push({ content: line, type: 'added' });
      });
    } else if (part.removed) {
      // Removed lines: content on template side, blank on generated side
      lines.forEach(line => {
        templateLines.push({ content: line, type: 'removed' });
        generatedLines.push({ content: '', type: 'blank' });
      });
    } else {
      // Unchanged lines: content on both sides
      lines.forEach(line => {
        templateLines.push({ content: line, type: 'normal' });
        generatedLines.push({ content: line, type: 'normal' });
      });
    }
  });

  // For inline mode, just return the content without side-by-side structure
  if (inline) {
    const linesToShow = side === 'template' ? templateLines : generatedLines;
    const isTemplate = side === 'template';

    return (
      <Box
        component="pre"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          whiteSpace: 'pre-wrap',
          backgroundColor: '#f5f5f5',
          p: 2,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          margin: 0,
          maxHeight: '500px',
          overflow: 'auto',
          lineHeight: 1.6
        }}
      >
        {linesToShow.map((line, index) => (
          <Box
            key={index}
            component="div"
            sx={{
              backgroundColor: isTemplate
                ? (line.type === 'removed' ? '#f8d7da' : 'transparent')
                : (line.type === 'added' ? '#d4edda' : 'transparent'),
              color: isTemplate
                ? (line.type === 'removed' ? '#721c24' : 'inherit')
                : (line.type === 'added' ? '#155724' : 'inherit'),
              paddingLeft: 1,
              borderLeft: isTemplate
                ? (line.type === 'removed' ? '3px solid #dc3545' : 'none')
                : (line.type === 'added' ? '3px solid #28a745' : 'none'),
              minHeight: '1.6em'
            }}
          >
            {line.content || '\u00A0'}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Diff Summary */}
      {showSummary && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {addedCount > 0 && (
            <Chip
              label={`+${addedCount} lines added`}
              size="small"
              sx={{ backgroundColor: '#d4edda', color: '#155724' }}
            />
          )}
          {removedCount > 0 && (
            <Chip
              label={`-${removedCount} lines removed`}
              size="small"
              sx={{ backgroundColor: '#f8d7da', color: '#721c24' }}
            />
          )}
          {addedCount === 0 && removedCount === 0 && (
            <Chip
              label="No differences"
              size="small"
              sx={{ backgroundColor: '#d4edda', color: '#155724' }}
            />
          )}
        </Box>
      )}

      {/* Side-by-Side Diff View */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Template Side */}
        <Box sx={{ flex: 1 }}>
          {showHeaders && (
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
              Template
            </Typography>
          )}
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              backgroundColor: '#f5f5f5',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              margin: 0,
              maxHeight: '500px',
              overflow: 'auto',
              lineHeight: 1.6
            }}
          >
            {templateLines.map((line, index) => (
              <Box
                key={index}
                component="div"
                sx={{
                  backgroundColor: line.type === 'removed' ? '#f8d7da' : 'transparent',
                  color: line.type === 'removed' ? '#721c24' : 'inherit',
                  paddingLeft: 1,
                  borderLeft: line.type === 'removed' ? '3px solid #dc3545' : 'none',
                  minHeight: '1.6em'
                }}
              >
                {line.content || '\u00A0'}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Generated Side */}
        <Box sx={{ flex: 1 }}>
          {showHeaders && (
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'secondary.main' }}>
              Generated
            </Typography>
          )}
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              backgroundColor: '#f5f5f5',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              margin: 0,
              maxHeight: '500px',
              overflow: 'auto',
              lineHeight: 1.6
            }}
          >
            {generatedLines.map((line, index) => (
              <Box
                key={index}
                component="div"
                sx={{
                  backgroundColor: line.type === 'added' ? '#d4edda' : 'transparent',
                  color: line.type === 'added' ? '#155724' : 'inherit',
                  paddingLeft: 1,
                  borderLeft: line.type === 'added' ? '3px solid #28a745' : 'none',
                  minHeight: '1.6em'
                }}
              >
                {line.content || '\u00A0'}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ===============================================
// Main Component
// ===============================================

export default function PivotManagerTest() {
  // Mode state
  const [mode, setMode] = useState<'templates' | 'generated'>('templates');

  // Navigation state
  const [topNavOptions, setTopNavOptions] = useState<string[]>([]);
  const [leftNavOptions, setLeftNavOptions] = useState<Record<string, string[]>>({});
  const [viewOptions, setViewOptions] = useState<Record<string, string[]>>({});

  const [selectedTopNav, setSelectedTopNav] = useState<string>('');
  const [selectedLeftNav, setSelectedLeftNav] = useState<string>('');
  const [selectedView, setSelectedView] = useState<string>('');
  const [uniqueId, setUniqueId] = useState<string | null>(null);

  // Data state
  const [patterns, setPatterns] = useState<PatternSummary[]>([]);
  const [generatedPivots, setGeneratedPivots] = useState<GeneratedPivotSummary[]>([]);
  const [currentPatternId, setCurrentPatternId] = useState<number | null>(null);
  const [currentGeneratedId, setCurrentGeneratedId] = useState<number | null>(null);
  const [currentPivotName, setCurrentPivotName] = useState<string | null>(null);
  const [header, setHeader] = useState<HeaderModel | null>(null);
  const [sections, setSections] = useState<OrderedSection[]>([]);

  // Loading states
  const [loadingNavigation, setLoadingNavigation] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [generating, setGenerating] = useState(false);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<SectionItem | null>(null);
  const [viewMode, setViewMode] = useState<'sections' | 'items' | 'item-detail'>('sections');
  const [searchTerm, setSearchTerm] = useState('');

  // Comparison state
  const [compareMode, setCompareMode] = useState(false);
  const [compareData, setCompareData] = useState<any>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Edit state
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // OCI state
  const [ociAvailable, setOciAvailable] = useState(false);
  const [ociSyncing, setOciSyncing] = useState(false);

  // Check OCI availability on mount
  useEffect(() => {
    const checkOCI = async () => {
      try {
        const response = await axios.get('/patterns/oci/check');
        setOciAvailable(response.data.available);
      } catch (error) {
        console.error('Failed to check OCI availability:', error);
        setOciAvailable(false);
      }
    };
    checkOCI();
  }, []);

  // Load navigation options on mount
  useEffect(() => {
    const loadNavigation = async () => {
      try {
        setLoadingNavigation(true);
        const navData = await fetchNavigationOptions();
        setTopNavOptions(navData.topNavs);
        setLeftNavOptions(navData.leftNavs);
        setViewOptions(navData.views);

        // Set first options as default
        if (navData.topNavs.length > 0) {
          const firstTopNav = navData.topNavs[0];
          setSelectedTopNav(firstTopNav);

          if (navData.leftNavs[firstTopNav]?.length > 0) {
            const firstLeftNav = navData.leftNavs[firstTopNav][0];
            setSelectedLeftNav(firstLeftNav);

            const viewKey = `${firstTopNav}|${firstLeftNav}`;
            if (navData.views[viewKey]?.length > 0) {
              setSelectedView(navData.views[viewKey][0]);
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load navigation');
      } finally {
        setLoadingNavigation(false);
      }
    };

    loadNavigation();
  }, []);

  const loadGeneratedData = async () => {
    try {
      setLoadingData(true);
      setError(null);

      const pivots = await fetchGeneratedPivots(selectedTopNav, selectedLeftNav, selectedView);
      setGeneratedPivots(pivots);

      // Don't auto-load any pivot, just show the cards
      setSections([]);
      setHeader(null);
      setCurrentPatternId(null);
      setCurrentGeneratedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load generated data');
      setGeneratedPivots([]);
    } finally {
      setLoadingData(false);
    }
  };

  const loadTemplateData = async () => {
    try {
      setLoadingData(true);
      setError(null);

      // For templates mode, fetch pivots for this navigation
      const pivots = await fetchGeneratedPivots(selectedTopNav, selectedLeftNav, selectedView);
      setGeneratedPivots(pivots);

      // Don't auto-load any pivot, just show the cards
      setSections([]);
      setHeader(null);
      setCurrentPatternId(null);
      setCurrentGeneratedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template data');
      setGeneratedPivots([]);
    } finally {
      setLoadingData(false);
    }
  };

  // Load patterns when in template mode
  useEffect(() => {
    if (mode === 'templates' && selectedTopNav && selectedLeftNav && selectedView) {
      loadTemplateData();
    }
  }, [mode, selectedTopNav, selectedLeftNav, selectedView, loadTemplateData]);

  // Load generated pivots when in generated mode
  useEffect(() => {
    if (mode === 'generated' && selectedTopNav && selectedLeftNav && selectedView) {
      loadGeneratedData();
    }
  }, [mode, selectedTopNav, selectedLeftNav, selectedView, loadGeneratedData]);

  const handleModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'templates' | 'generated' | null) => {
    if (newMode !== null) {
      setMode(newMode);
      setSelectedSection('all');
      setViewMode('sections');
      setSelectedItem(null);
    }
  };

  const handleTopNavChange = (value: string) => {
    setSelectedTopNav(value);
    setSelectedLeftNav('');
    setSelectedView('');

    if (leftNavOptions[value]?.length > 0) {
      setSelectedLeftNav(leftNavOptions[value][0]);

      const viewKey = `${value}|${leftNavOptions[value][0]}`;
      if (viewOptions[viewKey]?.length > 0) {
        setSelectedView(viewOptions[viewKey][0]);
      }
    }
  };

  const handleLeftNavChange = (value: string) => {
    setSelectedLeftNav(value);
    setSelectedView('');

    const viewKey = `${selectedTopNav}|${value}`;
    if (viewOptions[viewKey]?.length > 0) {
      setSelectedView(viewOptions[viewKey][0]);
    }
  };

  const handleGeneratePivots = async () => {
    setGenerating(true);
    setError(null);
    setSuccess('Starting pivot generation in background...');

    try {
      const result = await generatePivots();

      if (result.success && result.job_id) {
        // Poll for job status
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(`/generate-pivots/${result.job_id}`);
            const job = statusResponse.data.job;

            if (job.status === 'running') {
              // Only show clean progress, filter out verbose output
              const progress = job.progress || '';
              let cleanMsg = 'Generating pivots...';

              if (progress.includes('PHASE 1') || progress.includes('Generating pivot files')) {
                cleanMsg = 'Phase 1: Generating pivot files...';
              } else if (progress.includes('PHASE 2') || progress.includes('Parsing generated files')) {
                cleanMsg = 'Phase 2: Parsing files...';
              } else if (progress.includes('PHASE 3') || progress.includes('Loading to database')) {
                cleanMsg = 'Phase 3: Loading to database...';
              } else if (progress.match(/Generated \d+ pivots/)) {
                cleanMsg = progress;
              }

              setSuccess(cleanMsg);
            } else if (job.status === 'completed') {
              clearInterval(pollInterval);
              setSuccess(`Successfully generated ${job.pivots_generated || 0} pivots with ${job.parts_generated || 0} parts`);
              setGenerating(false);

              // Refresh data
              if (mode === 'generated') {
                await loadGeneratedData();
              } else {
                await loadTemplateData();
              }
            } else if (job.status === 'failed') {
              clearInterval(pollInterval);
              setError(`Pivot generation failed: ${job.error || 'Unknown error'}`);
              setGenerating(false);
            }
          } catch (err) {
            clearInterval(pollInterval);
            setError('Failed to check generation status');
            setGenerating(false);
          }
        }, 2000); // Poll every 2 seconds

        // Set a timeout to stop polling after 10 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (generating) {
            setSuccess('Pivot generation is taking longer than expected. Check server logs for progress.');
            setGenerating(false);
          }
        }, 600000);

      } else {
        setError(result.error || 'Failed to start pivot generation');
        setGenerating(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate pivots');
      setGenerating(false);
    }
  };

  const handleSectionChange = (sectionName: string) => {
    setSelectedSection(sectionName);
    // Always stay in 'sections' mode when using dropdown
    // 'items' mode is only for when clicking Section Overview cards
    setViewMode('sections');
    setSelectedItem(null);
  };

  const handleSectionSelect = (sectionName: string) => {
    setSelectedSection(sectionName);
    setViewMode('items');
    setSelectedItem(null);
  };

  const handleItemSelect = (item: SectionItem) => {
    setSelectedItem(item);
    setViewMode('item-detail');
  };

  const handleBackToSections = () => {
    setSelectedSection('all');
    setViewMode('sections');
    setSelectedItem(null);
  };

  const handleBackToItems = () => {
    setViewMode('items');
    setSelectedItem(null);
  };

  const handleRefresh = () => {
    if (mode === 'templates') {
      loadTemplateData();
    } else {
      loadGeneratedData();
    }
  };

  const handlePivotCardClick = async (pivot: GeneratedPivotSummary) => {
    try {
      setLoadingData(true);
      setError(null);

      if (mode === 'templates') {
        // Load template pattern
        if (pivot.template_pattern_id) {
          const [headerData, sectionsData, templateData] = await Promise.all([
            fetchHeader(pivot.template_pattern_id),
            fetchSections(pivot.template_pattern_id),
            axios.get(`/patterns/${pivot.template_pattern_id}`)
          ]);
          setHeader(headerData);
          setSections(sectionsData);
          setCurrentPatternId(pivot.template_pattern_id);
          setCurrentGeneratedId(null);
          // Use pattern_key from template for display
          setCurrentPivotName(templateData.data.pattern_key || pivot.pivot_name);
        } else {
          // No template, show generated
          const { sections: sectionsData, header: headerData } = await fetchGeneratedSections(pivot.generated_pattern_id);
          setSections(sectionsData);
          setHeader(headerData);
          setCurrentGeneratedId(pivot.generated_pattern_id);
          setCurrentPatternId(null);
          setCurrentPivotName(pivot.pivot_name);
        }
      } else {
        // Load generated pivot
        const { sections: sectionsData, header: headerData } = await fetchGeneratedSections(pivot.generated_pattern_id);
        setSections(sectionsData);
        setHeader(headerData);
        setCurrentGeneratedId(pivot.generated_pattern_id);
        setCurrentPatternId(null);
        setCurrentPivotName(pivot.pivot_name);
      }

      // Reset view state
      setSelectedSection('all');
      setViewMode('sections');
      setSelectedItem(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pivot details');
    } finally {
      setLoadingData(false);
    }
  };

  const handleBackToPivots = () => {
    setSections([]);
    setHeader(null);
    setCurrentPatternId(null);
    setCurrentGeneratedId(null);
    setCurrentPivotName(null);
    setSelectedSection('all');
    setViewMode('sections');
    setSelectedItem(null);
    setCompareMode(false);
    setCompareData(null);
  };

  const handleCompare = async () => {
    if (!currentGeneratedId) return;

    try {
      setLoadingData(true);
      setError(null);

      const response = await axios.get(`/generated-pivots/${currentGeneratedId}/compare`);
      setCompareData(response.data);
      setCompareMode(true);
      // Keep the current section selection and view mode when entering compare mode
      // If no section is selected yet, default to the first available section
      if (selectedSection === 'all' && response.data.template.sections.length > 0) {
        setSelectedSection(response.data.template.sections[0].section);
      }
      // Don't reset viewMode - keep the current drill-down state
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comparison');
    } finally {
      setLoadingData(false);
    }
  };

  const handleExitCompare = () => {
    setCompareMode(false);
    setCompareData(null);
    setExpandedItems(new Set());
  };

  const handleExportPivot = async () => {
    const patternId = mode === 'templates' ? currentPatternId : currentGeneratedId;
    if (!patternId) return;

    try {
      const response = await axios.get(`/patterns/${patternId}/export`, {
        responseType: 'blob'
      });

      // Create a download link
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'pivot.pivotdefn';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('Pivot exported successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export pivot';
      setError(errorMessage);
    }
  };

  const handleSyncToOCI = async () => {
    const patternId = mode === 'templates' ? currentPatternId : currentGeneratedId;
    if (!patternId) return;

    if (!window.confirm('This will sync the pivot to OCI. Continue?')) {
      return;
    }

    try {
      setOciSyncing(true);

      const response = await axios.post(`/patterns/${patternId}/sync-to-oci`);

      if (response.data.success) {
        alert('Pivot synced to OCI successfully!');
      } else {
        throw new Error(response.data.error || 'Sync failed');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to sync to OCI';
      alert(`Sync failed: ${errorMessage}`);
      console.error('OCI sync error:', err);
    } finally {
      setOciSyncing(false);
    }
  };

  const toggleItemExpand = (itemName: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemName)) {
      newExpanded.delete(itemName);
    } else {
      newExpanded.add(itemName);
    }
    setExpandedItems(newExpanded);
  };

  const handleEditItem = (itemName: string, content: string) => {
    setEditingItem(itemName);
    setEditContent(content);
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditContent('');
  };

  const handleSaveEdit = async (itemName: string) => {
    if (!currentGeneratedId) return;

    try {
      setSaving(true);
      setError(null);

      await axios.put(`/generated-pivots/${currentGeneratedId}/parts/${encodeURIComponent(itemName)}`, {
        content: editContent,
        edited_by: 'user' // TODO: Get from auth context
      });

      // Refresh the sections data
      const { sections: sectionsData, header: headerData } = await fetchGeneratedSections(currentGeneratedId);
      setSections(sectionsData);
      setHeader(headerData);

      setSuccess('Content saved successfully');
      setEditingItem(null);
      setEditContent('');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  // Filter sections based on selection
  const filteredSections = useMemo(() => {
    if (selectedSection === 'all') {
      return sections;
    }
    return sections.filter(section => section.section === selectedSection);
  }, [sections, selectedSection]);

  // Get available section options for the dropdown
  const sectionOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All sections' }];
    sections.forEach(section => {
      options.push({ value: section.section, label: section.section });
    });
    return options;
  }, [sections]);

  const availableLeftNavs = useMemo(() => leftNavOptions[selectedTopNav] || [], [leftNavOptions, selectedTopNav]);
  const availableViews = useMemo(() => viewOptions[`${selectedTopNav}|${selectedLeftNav}`] || [], [viewOptions, selectedTopNav, selectedLeftNav]);

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Pivot Manager (Enhanced)
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View templates and generated pivots with hierarchical navigation
        </Typography>
      </Box>

      {/* Error/Success Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          {/* Mode Toggle */}
          <Box>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Mode
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              size="small"
            >
              <ToggleButton value="templates">Templates</ToggleButton>
              <ToggleButton value="generated">Generated Pivots</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Navigation Dropdowns */}
          <Box sx={{ display: 'flex', gap: 2, flex: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Top Nav</InputLabel>
              <Select
                value={selectedTopNav}
                onChange={(e) => handleTopNavChange(e.target.value)}
                label="Top Nav"
              >
                {topNavOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Left Nav</InputLabel>
              <Select
                value={selectedLeftNav}
                onChange={(e) => handleLeftNavChange(e.target.value)}
                label="Left Nav"
                disabled={!selectedTopNav}
              >
                {availableLeftNavs.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>View</InputLabel>
              <Select
                value={selectedView}
                onChange={(e) => setSelectedView(e.target.value)}
                label="View"
                disabled={!selectedLeftNav}
              >
                {availableViews.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Generate Button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <GenerateIcon />}
            onClick={handleGeneratePivots}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Generate Pivots'}
          </Button>
        </Box>
      </Paper>

      {/* Content Area */}
      {loadingNavigation ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : !selectedTopNav || !selectedLeftNav || !selectedView ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <Typography variant="body1" color="text.secondary">
            Select navigation options to view patterns
          </Typography>
        </Box>
      ) : currentPatternId || currentGeneratedId ? (
        <>
          {/* Back to Pivots Button */}
          <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToPivots}
                size="small"
              >
                Back to Pivot List
              </Button>
              {!compareMode && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportPivot}
                  size="small"
                >
                  Export Pivot
                </Button>
              )}
              {!compareMode && ociAvailable && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleSyncToOCI}
                  disabled={ociSyncing}
                  size="small"
                >
                  {ociSyncing ? 'Syncing...' : 'Sync to OCI'}
                </Button>
              )}
              {currentGeneratedId && !compareMode && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<CompareIcon />}
                  onClick={handleCompare}
                  size="small"
                >
                  Compare with Template
                </Button>
              )}
              {compareMode && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<CancelIcon />}
                  onClick={handleExitCompare}
                  size="small"
                >
                  Exit Compare Mode
                </Button>
              )}
            </Box>
          </Paper>

          {loadingData ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          ) : compareMode && compareData && viewMode === 'item-detail' && selectedItem ? (
            <>
              {/* Item-level comparison with expandable diff view */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Pivot Item Comparison
                </Typography>

                {/* Item Name and Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {selectedItem.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Section: {selectedSection}
                  </Typography>
                </Box>

                {(() => {
                  const section = compareData.template.sections.find((s: any) => s.section === selectedSection);
                  const templateItem = section?.items.find((i: any) => i.name === selectedItem.name);

                  if (!templateItem) {
                    return (
                      <Alert severity="warning">
                        This item does not exist in the template. It only exists in the generated pivot.
                      </Alert>
                    );
                  }

                  const isExpanded = expandedItems.has(selectedItem.name);
                  const hasDiff = templateItem.content !== selectedItem.content;

                  return (
                    <Box>
                      <Box
                        onClick={() => toggleItemExpand(selectedItem.name)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          p: 2,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' },
                          mb: 2
                        }}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        <Typography sx={{ ml: 1, flexGrow: 1, fontWeight: 'medium' }}>
                          {selectedItem.name}
                        </Typography>
                        {hasDiff && (
                          <Chip
                            label="Has differences"
                            size="small"
                            color="warning"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                      <Collapse in={isExpanded}>
                        <Box>
                          <SideBySideDiffViewer
                            templateContent={templateItem.content}
                            generatedContent={selectedItem.content}
                            itemName={selectedItem.name}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })()}
              </Paper>
            </>
          ) : compareMode && compareData ? (
            <>
              {/* Pivot Name Header */}
              {currentPivotName && (
                <Paper sx={{ p: 2, mb: 2, backgroundColor: 'primary.main', color: 'white' }}>
                  <Typography variant="h5" fontWeight="bold">
                    Comparing Generated Pivot: {currentPivotName}
                  </Typography>
                </Paper>
              )}

              {/* Section-level comparison - Model Manager style */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Pivot Section Comparison
                </Typography>

                {/* Section Selector */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    Compare Section:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={selectedSection}
                      onChange={(e) => setSelectedSection(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="all">All sections</MenuItem>
                      {Array.from(new Set([
                        ...compareData.template.sections.map((s: any) => s.section),
                        ...compareData.generated.sections.map((s: any) => s.section)
                      ])).sort().map((sectionName: any) => (
                        <MenuItem key={sectionName} value={sectionName}>
                          {sectionName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Comparison Summary */}
                {selectedSection !== 'all' && (() => {
                  const templateSection = compareData.template.sections.find((s: any) => s.section === selectedSection);
                  const generatedSection = compareData.generated.sections.find((s: any) => s.section === selectedSection);
                  const templateItems = templateSection?.items || [];
                  const generatedItems = generatedSection?.items || [];

                  const templateNames = new Set(templateItems.map((i: any) => i.name));
                  const generatedNames = new Set(generatedItems.map((i: any) => i.name));

                  const matched = templateItems.filter((i: any) => generatedNames.has(i.name)).length;
                  const onlyInTemplate = templateItems.filter((i: any) => !generatedNames.has(i.name)).length;
                  const onlyInGenerated = generatedItems.filter((i: any) => !templateNames.has(i.name)).length;

                  return (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Comparison Summary
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={`Template: ${templateItems.length} items`} sx={{ backgroundColor: 'grey.300' }} />
                        <Chip label={`Generated: ${generatedItems.length} items`} sx={{ backgroundColor: 'info.main', color: 'white' }} />
                        <Chip label={`Matched: ${matched}`} sx={{ backgroundColor: 'success.main', color: 'white' }} />
                        <Chip label={`Only in Template: ${onlyInTemplate}`} sx={{ backgroundColor: 'error.main', color: 'white' }} />
                        <Chip label={`Only in Generated: ${onlyInGenerated}`} sx={{ backgroundColor: 'warning.main', color: 'white' }} />
                      </Box>
                    </Box>
                  );
                })()}

                {/* Two-column comparison table */}
                {selectedSection === 'all' ? (
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                      Please select a specific section to compare
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Use the dropdown above to choose a section (PARAM, PROLOGUE, AGG_SQL, etc.)
                    </Typography>
                  </Paper>
                ) : (() => {
                  const templateSection = compareData.template.sections.find((s: any) => s.section === selectedSection);
                  const generatedSection = compareData.generated.sections.find((s: any) => s.section === selectedSection);
                  const templateItems = templateSection?.items || [];
                  const generatedItems = generatedSection?.items || [];

                  const templateNames = new Set(templateItems.map((i: any) => i.name));
                  const generatedNames = new Set(generatedItems.map((i: any) => i.name));

                  return (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      {/* Template Side */}
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Template: {selectedSection}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 2 }}>
                          <Typography variant="body2" fontWeight="bold">Item Name</Typography>
                          <Typography variant="body2" fontWeight="bold">Status</Typography>
                        </Box>
                        <Box sx={{ maxHeight: '600px', overflow: 'auto' }}>
                          {templateItems.map((item: any, idx: number) => {
                            const isMatched = generatedNames.has(item.name);
                            const matchedItem = generatedItems.find((g: any) => g.name === item.name);
                            const isExpanded = expandedItems.has(item.name);
                            const hasDiff = isMatched && item.content !== matchedItem?.content;

                            return (
                              <Box
                                key={idx}
                                sx={{
                                  backgroundColor: isMatched ? 'white' : '#ffe6e6',
                                  mb: 1,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider'
                                }}
                              >
                                <Box
                                  sx={{
                                    p: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: isMatched ? 'pointer' : 'default',
                                    '&:hover': isMatched ? { backgroundColor: '#f5f5f5' } : {}
                                  }}
                                  onClick={() => isMatched && toggleItemExpand(item.name)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isMatched && (isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
                                    <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {hasDiff && (
                                      <Chip
                                        label="Has differences"
                                        size="small"
                                        sx={{ backgroundColor: 'warning.light', color: 'warning.dark' }}
                                      />
                                    )}
                                    <Chip
                                      label={isMatched ? 'Matched' : 'Not in Generated'}
                                      size="small"
                                      sx={{
                                        backgroundColor: isMatched ? 'success.main' : 'error.main',
                                        color: 'white'
                                      }}
                                    />
                                  </Box>
                                </Box>
                                {isMatched && isExpanded && matchedItem && (
                                  <Collapse in={isExpanded}>
                                    <Box sx={{ p: 2, pt: 0, borderTop: '1px solid', borderColor: 'divider' }}>
                                      <SideBySideDiffViewer
                                        templateContent={item.content}
                                        generatedContent={matchedItem.content}
                                        itemName={item.name}
                                        showHeaders={false}
                                        showSummary={false}
                                        inline={true}
                                        side="template"
                                      />
                                    </Box>
                                  </Collapse>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Paper>

                      {/* Generated Side */}
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Generated: {selectedSection}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, borderBottom: '2px solid', borderColor: 'divider', pb: 1, mb: 2 }}>
                          <Typography variant="body2" fontWeight="bold">Item Name</Typography>
                          <Typography variant="body2" fontWeight="bold">Status</Typography>
                        </Box>
                        <Box sx={{ maxHeight: '600px', overflow: 'auto' }}>
                          {generatedItems.map((item: any, idx: number) => {
                            const isMatched = templateNames.has(item.name);
                            const matchedItem = templateItems.find((t: any) => t.name === item.name);
                            const isExpanded = expandedItems.has(item.name);
                            const hasDiff = isMatched && item.content !== matchedItem?.content;

                            return (
                              <Box
                                key={idx}
                                sx={{
                                  backgroundColor: isMatched ? 'white' : '#fff3e0',
                                  mb: 1,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider'
                                }}
                              >
                                <Box
                                  sx={{
                                    p: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: isMatched ? 'pointer' : 'default',
                                    '&:hover': isMatched ? { backgroundColor: '#f5f5f5' } : {}
                                  }}
                                  onClick={() => isMatched && toggleItemExpand(item.name)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isMatched && (isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
                                    <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {hasDiff && (
                                      <Chip
                                        label="Has differences"
                                        size="small"
                                        sx={{ backgroundColor: 'warning.light', color: 'warning.dark' }}
                                      />
                                    )}
                                    <Chip
                                      label={isMatched ? 'Matched' : 'Only in Generated'}
                                      size="small"
                                      sx={{
                                        backgroundColor: isMatched ? 'success.main' : 'warning.main',
                                        color: 'white'
                                      }}
                                    />
                                  </Box>
                                </Box>
                                {isMatched && isExpanded && matchedItem && (
                                  <Collapse in={isExpanded}>
                                    <Box sx={{ p: 2, pt: 0, borderTop: '1px solid', borderColor: 'divider' }}>
                                      <SideBySideDiffViewer
                                        templateContent={matchedItem.content}
                                        generatedContent={item.content}
                                        itemName={item.name}
                                        showHeaders={false}
                                        showSummary={false}
                                        inline={true}
                                        side="generated"
                                      />
                                    </Box>
                                  </Collapse>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Paper>
                    </Box>
                  );
                })()}
              </Paper>
            </>
          ) : (
            <>
              {/* Pivot Name Header */}
              {currentPivotName && (
                <Paper sx={{ p: 2, mb: 2, backgroundColor: 'primary.main', color: 'white' }}>
                  <Typography variant="h5" fontWeight="bold">
                    {mode === 'templates' ? 'Template: ' : 'Generated Pivot: '}
                    {currentPivotName}
                  </Typography>
                </Paper>
              )}

              {/* Section Selector - Only show when viewMode is 'sections' */}
              {sections.length > 0 && viewMode === 'sections' && (
                <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.paper' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" fontWeight="medium" sx={{ minWidth: 'auto' }}>
                      Working on:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={selectedSection}
                        onChange={(e) => handleSectionChange(e.target.value)}
                        displayEmpty
                      >
                        {sectionOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {selectedSection !== 'all' && (
                      <Typography variant="body2" color="text.secondary">
                        ({filteredSections.length > 0 ? filteredSections[0].items.length : 0} items)
                      </Typography>
                    )}
                    {selectedSection === 'all' && (
                      <Typography variant="body2" color="text.secondary">
                        ({sections.length} sections)
                      </Typography>
                    )}
                  </Box>
                </Paper>
              )}

              {/* Header Card */}
              {header && <HeaderCard header={header} />}

              {/* Content Views */}
              {viewMode === 'sections' && selectedSection === 'all' ? (
                <>
                  {/* Show Section Overview cards when "All sections" is selected */}
                  <SectionOverviewCard
                    sections={sections}
                    onSectionSelect={handleSectionSelect}
                  />
                  {/* Show all sections */}
                  {sections.map((section) => (
                    <SectionComponent
                      key={section.section}
                      patternId={mode === 'templates' ? currentPatternId! : currentGeneratedId!}
                      section={section}
                      onRefresh={handleRefresh}
                      mode={mode}
                      editingItem={editingItem}
                      editContent={editContent}
                      onEdit={handleEditItem}
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                      onEditContentChange={setEditContent}
                      saving={saving}
                    />
                  ))}
                </>
              ) : viewMode === 'sections' && selectedSection !== 'all' ? (
                <>
                  {/* When specific section selected from dropdown, show ALL items in that section */}
                  {filteredSections.map((section) => (
                    <SectionComponent
                      key={section.section}
                      patternId={mode === 'templates' ? currentPatternId! : currentGeneratedId!}
                      section={section}
                      onRefresh={handleRefresh}
                      mode={mode}
                      editingItem={editingItem}
                      editContent={editContent}
                      onEdit={handleEditItem}
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                      onEditContentChange={setEditContent}
                      saving={saving}
                    />
                  ))}
                </>
              ) : viewMode === 'items' && selectedSection !== 'all' ? (
                <SubsectionOverview
                  section={sections.find(s => s.section === selectedSection)!}
                  onItemSelect={handleItemSelect}
                  onBackToSections={handleBackToSections}
                />
              ) : viewMode === 'item-detail' && selectedItem ? (
                <ItemDetailView
                  item={selectedItem}
                  sectionName={selectedSection}
                  onBackToItems={handleBackToItems}
                  onRefresh={handleRefresh}
                  mode={mode}
                  editingItem={editingItem}
                  editContent={editContent}
                  onEdit={handleEditItem}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                  onEditContentChange={setEditContent}
                  saving={saving}
                />
              ) : null}
            </>
          )}
        </>
      ) : (
        <>
          {/* Pivot Cards Grid */}
          {loadingData ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          ) : generatedPivots.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No {mode === 'templates' ? 'templates' : 'generated pivots'} found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {mode === 'generated'
                  ? 'Click "Generate Pivots" to create runtime pivots from templates'
                  : 'No templates available for this navigation path'}
              </Typography>
            </Paper>
          ) : (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {mode === 'templates' ? 'Template Pivots' : 'Generated Pivots'} ({generatedPivots.length})
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                {generatedPivots.map((pivot) => (
                  <Paper
                    key={pivot.generated_pattern_id}
                    variant="outlined"
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        elevation: 4,
                        transform: 'translateY(-2px)',
                        borderColor: 'primary.main',
                      },
                    }}
                    onClick={() => handlePivotCardClick(pivot)}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom noWrap>
                      {mode === 'templates' ? (pivot.template_name || pivot.pivot_name) : pivot.pivot_name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      <Chip label={`ID: ${pivot.unique_id}`} size="small" variant="outlined" />
                      <Chip label={`${pivot.part_count} parts`} size="small" color="primary" />
                      {mode === 'generated' && (
                        <Chip label="Generated" size="small" color="success" />
                      )}
                      {mode === 'templates' && (
                        <Chip label="Template" size="small" color="info" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(pivot.created_at).toLocaleString()}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
